import React from 'react';
import { authFetch } from '../authFetch';
import { getFileServeUrl, getFileThumbnailUrl } from '../workspace';

type PreviewVariant = 'thumbnail' | 'serve';

interface UseWorkspaceImagePreviewOptions {
  fileId: string | null;
  variant?: PreviewVariant;
  enabled?: boolean;
  fallbackUrl?: string | null;
  allowServeFallback?: boolean;
}

interface PreviewCacheEntry {
  objectUrl: string | null;
  promise: Promise<string | null> | null;
  refCount: number;
  cleanupTimer: number | null;
}

const PREVIEW_CACHE_TTL_MS = 20_000;
const previewCache = new Map<string, PreviewCacheEntry>();

function buildCacheKey(fileId: string, variant: PreviewVariant): string {
  return `${variant}:${fileId}`;
}

function getAttemptPlan(fileId: string, variant: PreviewVariant, allowServeFallback: boolean): Array<{
  key: string;
  url: string;
  delayMs: number;
}> {
  if (variant === 'serve') {
    return [
      {
        key: buildCacheKey(fileId, 'serve'),
        url: getFileServeUrl(fileId),
        delayMs: 0,
      },
    ];
  }

  return [
    {
      key: buildCacheKey(fileId, 'thumbnail'),
      url: getFileThumbnailUrl(fileId),
      delayMs: 0,
    },
    {
      key: buildCacheKey(fileId, 'thumbnail'),
      url: getFileThumbnailUrl(fileId),
      delayMs: 180,
    },
    ...(allowServeFallback
      ? [{
          key: buildCacheKey(fileId, 'serve'),
          url: getFileServeUrl(fileId),
          delayMs: 0,
        }]
      : []),
  ];
}

async function wait(delayMs: number): Promise<void> {
  if (delayMs <= 0) return;
  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

function scheduleEntryCleanup(cacheKey: string) {
  const entry = previewCache.get(cacheKey);
  if (!entry) return;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
  }
  entry.cleanupTimer = window.setTimeout(() => {
    const latest = previewCache.get(cacheKey);
    if (!latest || latest.refCount > 0) return;
    if (latest.objectUrl) {
      URL.revokeObjectURL(latest.objectUrl);
    }
    previewCache.delete(cacheKey);
  }, PREVIEW_CACHE_TTL_MS);
}

async function retainPreview(cacheKey: string, url: string, delayMs: number): Promise<string | null> {
  let entry = previewCache.get(cacheKey);
  if (!entry) {
    entry = {
      objectUrl: null,
      promise: null,
      refCount: 0,
      cleanupTimer: null,
    };
    previewCache.set(cacheKey, entry);
  }

  entry.refCount += 1;
  if (entry.cleanupTimer !== null) {
    window.clearTimeout(entry.cleanupTimer);
    entry.cleanupTimer = null;
  }

  if (entry.objectUrl) {
    return entry.objectUrl;
  }

  if (!entry.promise) {
    entry.promise = (async () => {
      await wait(delayMs);
      const res = await authFetch(url);
      if (!res.ok) {
        throw new Error(`preview ${res.status}`);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const latest = previewCache.get(cacheKey);
      if (!latest) {
        URL.revokeObjectURL(objectUrl);
        return null;
      }
      latest.objectUrl = objectUrl;
      latest.promise = null;
      return objectUrl;
    })().catch(() => {
      const latest = previewCache.get(cacheKey);
      if (latest) {
        latest.promise = null;
      }
      return null;
    });
  }

  return entry.promise;
}

function releasePreview(cacheKey: string) {
  const entry = previewCache.get(cacheKey);
  if (!entry) return;
  entry.refCount = Math.max(0, entry.refCount - 1);
  if (entry.refCount === 0) {
    scheduleEntryCleanup(cacheKey);
  }
}

export function useWorkspaceImagePreview({
  fileId,
  variant = 'thumbnail',
  enabled = true,
  fallbackUrl = null,
  allowServeFallback = false,
}: UseWorkspaceImagePreviewOptions): string | null {
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(() => (
    fileId ? null : (fallbackUrl ?? null)
  ));

  React.useEffect(() => {
    if (!enabled) {
      setPreviewUrl(null);
      return;
    }

    if (!fileId) {
      setPreviewUrl(fallbackUrl ?? null);
      return;
    }

    let disposed = false;
    const retainedKeyCounts = new Map<string, number>();
    setPreviewUrl(null);

    const load = async () => {
      const attempts = getAttemptPlan(fileId, variant, allowServeFallback);
      for (const attempt of attempts) {
        const resolved = await retainPreview(attempt.key, attempt.url, attempt.delayMs);
        retainedKeyCounts.set(attempt.key, (retainedKeyCounts.get(attempt.key) ?? 0) + 1);
        if (disposed) return;
        if (resolved) {
          setPreviewUrl(resolved);
          return;
        }
      }
      if (!disposed) {
        setPreviewUrl(fallbackUrl ?? null);
      }
    };

    void load();

    return () => {
      disposed = true;
      retainedKeyCounts.forEach((count, cacheKey) => {
        for (let index = 0; index < count; index += 1) {
          releasePreview(cacheKey);
        }
      });
    };
  }, [allowServeFallback, enabled, fallbackUrl, fileId, variant]);

  return previewUrl ?? fallbackUrl ?? null;
}
